import React, { useCallback, useEffect, useRef, useState } from 'react';
import api from '../lib/api';

const GEO_KEY = 'sm_geo_choice';
const REFRESH_MS = 15 * 60 * 1000;

const tone = (w) => {
  if (w.kind === 'snow') return 'wx-snowy';
  if (w.temp >= 28) return 'wx-hot';
  if (w.temp >= 18) return 'wx-warm';
  if (w.temp >= 5) return 'wx-mild';
  return 'wx-cold';
};

const SunArt = () => (
  <svg viewBox="0 0 28 28" className="wx-art" aria-hidden="true">
    <circle cx="14" cy="14" r="9" className="wx-halo" />
    <g className="wx-rays">
      {[0, 45, 90, 135, 180, 225, 270, 315].map((a) => (
        <line key={a} x1="14" y1="3.2" x2="14" y2="6.2" transform={`rotate(${a} 14 14)`} />
      ))}
    </g>
    <circle cx="14" cy="14" r="5.4" className="wx-disc" />
  </svg>
);

const MoonArt = () => (
  <svg viewBox="0 0 28 28" className="wx-art" aria-hidden="true">
    <circle cx="14" cy="14" r="9" className="wx-halo" />
    <path d="M17.6 7.4a7 7 0 1 0 3.1 9.6A8 8 0 0 1 17.6 7.4Z" className="wx-moon" />
    {[[7, 8, 0], [21, 19, 0.9], [8.5, 20, 1.8]].map(([x, y, d]) => (
      <circle key={`${x}-${y}`} cx={x} cy={y} r="0.9" className="wx-star" style={{ animationDelay: `${d}s` }} />
    ))}
  </svg>
);

const CloudArt = () => (
  <svg viewBox="0 0 28 28" className="wx-art" aria-hidden="true">
    <path d="M6 19h12.4a3.6 3.6 0 0 0 .3-7.2A5.4 5.4 0 0 0 8.2 12A3.5 3.5 0 0 0 6 19Z" className="wx-cloud wx-drift-a" />
    <path d="M12 22.4h9.6a2.7 2.7 0 0 0 .2-5.4A4 4 0 0 0 13.6 18A2.6 2.6 0 0 0 12 22.4Z" className="wx-cloud wx-cloud-b wx-drift-b" />
  </svg>
);

const RainArt = ({ intensity = 1 }) => (
  <svg viewBox="0 0 28 28" className="wx-art" aria-hidden="true">
    <path d="M6 16h12.4a3.6 3.6 0 0 0 .3-7.2A5.4 5.4 0 0 0 8.2 9A3.5 3.5 0 0 0 6 16Z" className="wx-cloud" />
    {[8.5, 13.5, 18.5].map((x, i) => (
      <line key={x} x1={x} y1="17.5" x2={x - 1} y2="22" className="wx-drop"
        style={{ animationDuration: `${1.15 - intensity * 0.22}s`, animationDelay: `${i * 0.22}s` }} />
    ))}
  </svg>
);

const SnowArt = () => (
  <svg viewBox="0 0 28 28" className="wx-art" aria-hidden="true">
    <path d="M6 15h12.4a3.6 3.6 0 0 0 .3-7.2A5.4 5.4 0 0 0 8.2 8A3.5 3.5 0 0 0 6 15Z" className="wx-cloud" />
    {[[8.5, 0, 2.6], [13.5, 0.7, 2.1], [18.5, 1.3, 2.9]].map(([x, d, dur]) => (
      <g key={x} className="wx-flake" style={{ animationDelay: `${d}s`, animationDuration: `${dur}s` }}>
        <line x1={x} y1="16.4" x2={x} y2="19.4" />
        <line x1={x - 1.5} y1="17.9" x2={x + 1.5} y2="17.9" />
        <line x1={x - 1.1} y1="16.9" x2={x + 1.1} y2="18.9" />
        <line x1={x - 1.1} y1="18.9" x2={x + 1.1} y2="16.9" />
      </g>
    ))}
  </svg>
);

const StormArt = () => (
  <svg viewBox="0 0 28 28" className="wx-art" aria-hidden="true">
    <path d="M6 15h12.4a3.6 3.6 0 0 0 .3-7.2A5.4 5.4 0 0 0 8.2 8A3.5 3.5 0 0 0 6 15Z" className="wx-cloud" />
    <path d="M14.6 15.6 10.6 21h3.1l-1.1 4 5-6h-3.2l1.2-3.4Z" className="wx-bolt" />
  </svg>
);

const FogArt = () => (
  <svg viewBox="0 0 28 28" className="wx-art" aria-hidden="true">
    <path d="M7 12h11.4a3.3 3.3 0 0 0 .3-6.6A5 5 0 0 0 9 5.8A3.2 3.2 0 0 0 7 12Z" className="wx-cloud" />
    {[15.5, 18.5, 21.5].map((y, i) => (
      <line key={y} x1="5.5" y1={y} x2="22.5" y2={y} className="wx-fog" style={{ animationDelay: `${i * 0.5}s` }} />
    ))}
  </svg>
);

const Art = ({ w }) => {
  if (w.kind === 'rain') return <RainArt intensity={w.intensity} />;
  if (w.kind === 'snow') return <SnowArt />;
  if (w.kind === 'storm') return <StormArt />;
  if (w.kind === 'fog') return <FogArt />;
  if (w.kind === 'cloudy') return <CloudArt />;
  return w.is_day ? <SunArt /> : <MoonArt />;
};

export const WeatherChip = () => {
  const [w, setW] = useState(null);
  const coords = useRef(null);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get('/weather', { params: coords.current || {} });
      if (!data?.unavailable) setW({ ...data, at: new Date() });
    } catch (e) { /* mostrador silencioso: mantém a última leitura */ }
  }, []);

  useEffect(() => {
    let timer;
    const start = () => { load(); timer = setInterval(load, REFRESH_MS); };
    const choice = localStorage.getItem(GEO_KEY);
    if (choice === 'denied' || !navigator.geolocation) {
      start();
    } else {
      navigator.geolocation.getCurrentPosition(
        (p) => {
          localStorage.setItem(GEO_KEY, 'granted');
          coords.current = { lat: p.coords.latitude, lon: p.coords.longitude };
          start();
        },
        () => { localStorage.setItem(GEO_KEY, 'denied'); start(); },
        { timeout: 8000, maximumAge: 10 * 60 * 1000 },
      );
    }
    return () => clearInterval(timer);
  }, [load]);

  if (!w) return null;
  const hora = (w.at || new Date()).toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' });

  return (
    <div data-testid="weather-chip" className={`wx-chip ${tone(w)}`} title={`${w.label} · atualizado ${hora}`}>
      <Art w={w} />
      <span className="wx-temp num" data-testid="weather-temp">{w.temp}°C</span>
      {w.city && <span className="wx-city" data-testid="weather-city">{w.city}</span>}
    </div>
  );
};
